import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

/**
 * Member Management — list members, invite new, edit roles, remove.
 * Accessible from hamburger menu → "Team".
 */
export default function MemberManagement({ onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/v1/aif/members');
      setData(res?.data ?? res);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const removeMember = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/members/${id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
    } catch { /* ApiError */ }
    fetchMembers();
  };

  const cancelInvite = async (id) => {
    try {
      await apiClient.delete(`/api/v1/aif/members/invitations/${id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
    } catch { /* ApiError */ }
    fetchMembers();
  };

  const ROLE_LABELS = {
    owner: t('members.roleOwner'), manager: t('members.roleManager'),
    worker: t('members.roleWorker'), advisor: t('members.roleAdvisor'),
    accountant: t('members.roleAccountant'), family: t('members.roleFamily'),
  };

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
          </button>
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('members.title')}</span>
        </div>
        <button onClick={() => setShowInvite(true)} className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium">
          + {t('members.invite')}
        </button>
      </header>

      {showInvite && (
        <InviteForm onDone={() => { setShowInvite(false); fetchMembers(); }} onCancel={() => setShowInvite(false)} />
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>
        ) : (
          <>
            <p className="text-xs text-[var(--aif-text-muted)]">
              {data?.member_count || 0} {t('members.members')} &middot; {data?.invitation_count || 0} {t('members.pendingInvites')}
            </p>

            {/* Members */}
            {(data?.members || []).map(m => (
              <div key={m.membership_id} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[var(--aif-color-primary)] flex items-center justify-center text-white text-xs font-bold">
                      {(m.name || 'U')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[var(--aif-text-primary)]">{m.name}</div>
                      <div className="text-xs text-[var(--aif-text-muted)]">{m.email}</div>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] font-medium">
                    {ROLE_LABELS[m.role] || m.role}
                  </span>
                </div>
                {m.betrieb && <div className="text-xs text-[var(--aif-text-muted)]">{t('members.betrieb')}: {m.betrieb}</div>}
                {m.psm_valid_until && (
                  <div className="text-xs text-[var(--aif-text-muted)]">
                    {t('members.psmSachkunde')}: {'\u2705'} {t('members.validUntil')} {new Date(m.psm_valid_until).toLocaleDateString()}
                  </div>
                )}
                {m.role !== 'owner' && (
                  <div className="flex justify-end gap-2 pt-1">
                    <button onClick={() => removeMember(m.membership_id)} className="text-xs text-[var(--aif-color-danger)]">{t('members.remove')}</button>
                  </div>
                )}
              </div>
            ))}

            {/* Pending Invitations */}
            {(data?.invitations || []).length > 0 && (
              <>
                <div className="text-xs font-semibold text-[var(--aif-text-secondary)] pt-2">{t('members.pendingInvites')}</div>
                {data.invitations.map(inv => (
                  <div key={inv.id} className="bg-[var(--aif-bg-surface)] border border-dashed border-[var(--aif-border-default)] rounded-xl p-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--aif-text-primary)]">{inv.email || inv.phone}</span>
                      <span className="text-[10px] text-[var(--aif-text-muted)]">{ROLE_LABELS[inv.role] || inv.role}</span>
                    </div>
                    <div className="flex justify-end">
                      <button onClick={() => cancelInvite(inv.id)} className="text-xs text-[var(--aif-color-danger)]">{t('members.cancelInvite')}</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {(!data?.members?.length && !data?.invitations?.length) && (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">{'\uD83D\uDC65'}</div>
                <p className="text-sm text-[var(--aif-text-muted)]">{t('members.empty')}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InviteForm({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('worker');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleInvite = async () => {
    if (!email && !phone) return;
    setSaving(true);
    try {
      const data = await apiClient.post('/api/v1/aif/members/invite', { email: email || undefined, phone: phone || undefined, role }, { queueOnOffline: true });
      setResult(data);
    } catch { /* ApiError */ }
    setSaving(false);
  };

  const inputClass = "w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] placeholder-[var(--aif-text-muted)] focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]/30";

  if (result) {
    return (
      <div className="mx-4 my-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 space-y-3">
        <div className="text-sm font-medium text-green-700 dark:text-green-300">{'\u2705'} {t('members.inviteSent')}</div>
        {result.whatsapp_url && (
          <a href={result.whatsapp_url} target="_blank" rel="noopener noreferrer" className="block text-xs text-green-600 underline">{t('members.sendWhatsApp')}</a>
        )}
        <button onClick={onDone} className="text-xs text-[var(--aif-color-primary)]">{t('menu.close')}</button>
      </div>
    );
  }

  return (
    <div className="mx-4 my-3 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('members.inviteTitle')}</h4>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('members.emailPlaceholder')} className={inputClass} />
      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('members.phonePlaceholder')} className={inputClass} />
      <div className="flex gap-2 flex-wrap">
        {['worker', 'manager', 'advisor', 'accountant', 'family'].map(r => (
          <button key={r} onClick={() => setRole(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${role === r ? 'bg-[var(--aif-color-primary)] text-white' : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)]'}`}>
            {t(`members.role${r.charAt(0).toUpperCase() + r.slice(1)}`)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-lg text-sm text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]">{t('procurement.cancel')}</button>
        <button onClick={handleInvite} disabled={saving || (!email && !phone)} className="flex-1 py-2 rounded-lg text-sm text-white bg-[var(--aif-color-primary)] font-medium disabled:opacity-50">{saving ? '...' : t('members.sendInvite')}</button>
      </div>
    </div>
  );
}
